/**
 * Page /messages — Messagerie interne d'Emplois BNSSA & MNS.
 *
 * Layout :
 *   - Desktop : 2 colonnes (liste à gauche, conversation à droite)
 *   - Mobile : pleine largeur, bascule liste ↔ conversation
 *
 * Auth requise : redirige vers /connexion si non-connecté.
 */
import { useEffect, useMemo, useState, useRef, useCallback } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'
import {ArrowLeft, MessageSquare, Send, Shield, Flag, MoreVertical, Lock, Loader2, BadgeCheck} from 'lucide-react'
import toast from 'react-hot-toast'
import Layout from '@/components/Layout'
import Seo from '@/components/Seo'
import { lumi } from '@/lib/lumi'
import { useAuth } from '@/hooks/useAuth'
import {
  useMessaging,
  type Conversation,
  type Message,
  type MessageSenderRole,
} from '@/hooks/useMessaging'

function formatRelativeTime(iso?: string): string {
  if (!iso) return ''
  const d = new Date(iso).getTime()
  const diffMs = Date.now() - d
  const min = Math.floor(diffMs / 60000)
  if (min < 1) return "à l'instant"
  if (min < 60) return `il y a ${min} min`
  const h = Math.floor(min / 60)
  if (h < 24) return `il y a ${h} h`
  const days = Math.floor(h / 24)
  if (days < 7) return `il y a ${days} j`
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
}

function formatDateSeparator(iso: string): string {
  const d = new Date(iso)
  const today = new Date()
  const yesterday = new Date()
  yesterday.setDate(today.getDate() - 1)
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
  if (sameDay(d, today)) return "Aujourd'hui"
  if (sameDay(d, yesterday)) return 'Hier'
  return d.toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long' })
}

/** Initiales pour avatar (max 2 lettres). */
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

/** Couleur d'avatar selon le rôle : rouge BNSSA (recruteur), bleu MNS (candidat), ambre (organisme). */
function avatarClassForRole(role: MessageSenderRole): string {
  if (role === 'recruiter') return 'bg-red-100 text-red-700'
  if (role === 'organisme') return 'bg-amber-100 text-amber-700'
  if (role === 'admin') return 'bg-gray-200 text-gray-700'
  return 'bg-blue-100 text-blue-700'
}

function getPeerInfo(conv: Conversation, currentUserId: string) {
  const peerIdx = conv.participantIds.findIndex((id) => id !== currentUserId)
  if (peerIdx < 0) return { name: 'Inconnu', role: 'candidate' as MessageSenderRole }
  const role = (conv.participantRoles?.[peerIdx] || 'candidate') as MessageSenderRole
  let name = conv.participantNames?.[peerIdx] || ''
  // Anonymat CVthèque
  if (
    conv.context?.type === 'cvtheque' &&
    !conv.anonymityLifted &&
    role === 'candidate'
  ) {
    name = `Candidat ${conv.context.candidatePublicId || '#####'}`
  }
  if (!name) name = role === 'recruiter' ? 'Recruteur' : role === 'organisme' ? 'Organisme' : 'Candidat'
  return { name, role }
}

function accentColorForConv(conv: Conversation): string {
  // Rouge BNSSA / Bleu MNS / Neutre selon contexte
  const isJobAE = conv.context?.type === 'job_ae'
  if (isJobAE) return 'border-l-red-500'
  return 'border-l-blue-500'
}

export default function Messages() {
  const navigate = useNavigate()
  const { isAuthenticated, user, profile } = useAuth()
  const userId = user?.userId
  const [searchParams, setSearchParams] = useSearchParams()
  const initialConvId = searchParams.get('c')

  const {
    conversations,
    loading,
    error,
    listMessages,
    sendMessage,
    liftAnonymity,
    markAsRead,
    blockConversation,
    reportConversation,
  } = useMessaging(userId)

  const [activeConvId, setActiveConvId] = useState<string | null>(initialConvId)
  const [activeMessages, setActiveMessages] = useState<Message[]>([])
  const [composerValue, setComposerValue] = useState('')
  const [sending, setSending] = useState(false)
  const [showActions, setShowActions] = useState(false)
  const [revealing, setRevealing] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const composerRef = useRef<HTMLTextAreaElement>(null)
  const actionsRef = useRef<HTMLDivElement>(null)

  // Auth guard
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/connexion?next=/messages', { replace: true })
    }
  }, [isAuthenticated, navigate])

  // Sync URL param ↔ active conv
  useEffect(() => {
    if (activeConvId) {
      setSearchParams({ c: activeConvId }, { replace: true })
    } else {
      setSearchParams({}, { replace: true })
    }
  }, [activeConvId, setSearchParams])

  // Auto-select first conversation on desktop
  useEffect(() => {
    if (!activeConvId && conversations.length > 0 && window.innerWidth >= 768) {
      setActiveConvId(conversations[0]._id)
    }
  }, [conversations, activeConvId])

  const activeConv = useMemo(
    () => conversations.find((c) => c._id === activeConvId) || null,
    [conversations, activeConvId],
  )

  // Charge les messages quand on change de conversation, et marque comme lu
  const refreshMessages = useCallback(async () => {
    if (!activeConvId) {
      setActiveMessages([])
      return
    }
    const msgs = await listMessages(activeConvId)
    setActiveMessages(msgs)
    if (userId) markAsRead(activeConvId)
  }, [activeConvId, listMessages, markAsRead, userId])

  useEffect(() => {
    refreshMessages()
  }, [refreshMessages])

  // Polling messages (toutes les 10s) quand conversation active
  useEffect(() => {
    if (!activeConvId) return
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') refreshMessages()
    }, 10000)
    return () => clearInterval(t)
  }, [activeConvId, refreshMessages])

  // Auto-scroll en bas quand nouveaux messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [activeMessages.length])

  // Auto-resize du textarea du composer
  useEffect(() => {
    const el = composerRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 140)}px`
  }, [composerValue])

  // Ferme le menu d'actions au clic extérieur / touche Echap
  useEffect(() => {
    if (!showActions) return
    const onClick = (e: MouseEvent) => {
      if (actionsRef.current && !actionsRef.current.contains(e.target as Node)) {
        setShowActions(false)
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowActions(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [showActions])

  const currentRole: MessageSenderRole = useMemo(() => {
    if (profile?.role === 'recruiter') return 'recruiter'
    if (profile?.role === 'organisme') return 'organisme'
    return 'candidate'
  }, [profile])

  const handleSend = async (e?: React.FormEvent) => {
    e?.preventDefault()
    if (!activeConv || !composerValue.trim() || sending) return
    setSending(true)
    const sent = await sendMessage({
      conversation: activeConv,
      content: composerValue,
      currentRole,
      currentName: profile?.fullName || user?.userName || '',
    })
    setSending(false)
    if (sent) {
      setComposerValue('')
      await refreshMessages()
    } else {
      toast.error("Échec de l'envoi du message.")
    }
  }

  const handleRevealIdentity = async () => {
    if (!activeConv || revealing) return
    if (
      !confirm(
        'Révéler votre identité ? Le recruteur verra votre nom, prénom, téléphone et email. Cette action est définitive pour cette conversation.',
      )
    )
      return
    setRevealing(true)

    // Récupère les coordonnées réelles du candidat depuis son profil CVthèque
    let firstName = ''
    let lastName = ''
    let phone = ''
    let email = profile?.email || user?.email || ''
    try {
      const candidateId = activeConv.context?.availableCandidateId
      let candidateDoc: Record<string, unknown> | null = null
      if (candidateId) {
        candidateDoc = (await lumi.entities.available_candidates.get(candidateId)) as Record<
          string,
          unknown
        >
      }
      if (!candidateDoc && userId) {
        const { list } = await lumi.entities.available_candidates.list({
          filter: { creator: userId },
          sort: { updatedAt: -1 },
          limit: 1,
        })
        candidateDoc = (list?.[0] as Record<string, unknown>) || null
      }
      if (candidateDoc) {
        firstName = (candidateDoc.firstName as string) || ''
        lastName = (candidateDoc.lastName as string) || ''
        phone = (candidateDoc.phone as string) || ''
        email = (candidateDoc.email as string) || email
      }
    } catch (e) {
      console.warn('[Messages] récupération coordonnées candidat échouée:', e)
    }

    // Fallback nom : fullName du profil si prénom/nom absents du profil CVthèque
    const fullName = profile?.fullName || user?.userName || ''

    const ok = await liftAnonymity({
      conversation: activeConv,
      currentName: fullName,
      firstName,
      lastName,
      phone,
      email,
    })
    setRevealing(false)
    if (ok) {
      toast.success('Votre identité et vos coordonnées ont été transmises au recruteur.')
      await refreshMessages()
    } else {
      toast.error("Échec : impossible de révéler votre identité.")
    }
  }

  const handleBlock = async () => {
    if (!activeConv) return
    if (!confirm('Bloquer cette conversation ? Vous ne recevrez plus de messages de cet utilisateur.')) return
    await blockConversation(activeConv._id)
    toast.success('Conversation bloquée.')
    setShowActions(false)
    setActiveConvId(null)
  }

  const handleReport = async () => {
    if (!activeConv) return
    const reason = prompt('Décrivez brièvement le motif du signalement :')
    if (!reason || !reason.trim()) return
    await reportConversation(activeConv._id, reason.trim())
    toast.success('Signalement envoyé. Notre équipe examinera la conversation.')
    setShowActions(false)
  }

  if (!isAuthenticated || !userId) {
    return null
  }

  const showListMobile = !activeConvId

  return (
    <Layout>
      <Seo
        title="Messagerie interne | Emplois BNSSA & MNS"
        description="Échangez en toute sécurité avec recruteurs, candidats et organismes via la messagerie interne d'Emplois BNSSA & MNS."
        canonical="https://bnssa-mns.fr/messages"
      />
      <div className="min-h-screen bg-[#fafbfc]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-10">
          {/* Header de page (desktop) / cachée sur mobile en mode conversation */}
          <div className={`mb-6 ${!showListMobile ? 'hidden md:block' : ''}`}>
            <p className="eyebrow eyebrow-blue mb-2">Messagerie interne</p>
            <h1 className="headline-lg text-gray-900">Vos conversations</h1>
            <p className="body-lead text-gray-600 mt-2">
              Échangez en toute confidentialité avec les recruteurs, candidats et organismes.
            </p>
          </div>

          <div className="grid md:grid-cols-[340px_1fr] gap-0 md:gap-4 bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100">
            {/* ─── Liste des conversations ─── */}
            <aside
              className={`border-r border-gray-100 ${showListMobile ? 'block' : 'hidden md:block'}`}
            >
              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/60">
                <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-blue-600" aria-hidden="true" />
                  Conversations
                  {conversations.length > 0 && (
                    <span className="ml-auto text-xs font-medium text-gray-500">
                      {conversations.length}
                    </span>
                  )}
                </h2>
              </div>
              <div className="max-h-[70vh] overflow-y-auto">
                {loading && conversations.length === 0 ? (
                  <p className="p-6 text-sm text-gray-500 text-center">Chargement…</p>
                ) : error ? (
                  <p className="p-6 text-sm text-red-600 text-center">{error}</p>
                ) : conversations.length === 0 ? (
                  <div className="p-6 text-center">
                    <MessageSquare className="w-10 h-10 mx-auto text-gray-300 mb-3" aria-hidden="true" />
                    <p className="text-sm font-semibold text-gray-700">Aucune conversation</p>
                    <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                      Démarrez une discussion depuis la fiche d'un candidat (CVthèque) ou d'une offre auto-entrepreneur.
                    </p>
                    <Link
                      to="/disponibles"
                      className="inline-block mt-4 text-xs font-semibold text-blue-600 hover:underline"
                    >
                      Voir la CVthèque →
                    </Link>
                  </div>
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {conversations.map((conv) => {
                      const peer = getPeerInfo(conv, userId)
                      const unread = conv.unreadCounts?.[userId] || 0
                      const isActive = conv._id === activeConvId
                      return (
                        <li key={conv._id}>
                          <button
                            type="button"
                            onClick={() => setActiveConvId(conv._id)}
                            className={`w-full text-left px-3 py-3 border-l-4 ${accentColorForConv(conv)} hover:bg-gray-50 transition ${
                              isActive ? 'bg-blue-50/60' : ''
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div
                                className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-[13px] font-bold ${avatarClassForRole(peer.role)}`}
                                aria-hidden="true"
                              >
                                {getInitials(peer.name)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2 mb-0.5">
                                  <p className={`text-sm truncate ${unread > 0 ? 'font-extrabold text-gray-900' : 'font-bold text-gray-900'}`}>
                                    {peer.name}
                                  </p>
                                  <span className="text-[11px] text-gray-400 flex-shrink-0 mt-0.5">
                                    {formatRelativeTime(conv.lastMessageAt || conv.createdAt)}
                                  </span>
                                </div>
                                {conv.context?.jobTitle && (
                                  <p className="text-[11px] text-gray-500 truncate mb-0.5">
                                    À propos de : {conv.context.jobTitle}
                                  </p>
                                )}
                                <div className="flex items-center justify-between gap-2">
                                  <p
                                    className={`text-xs truncate ${
                                      unread > 0 ? 'text-gray-900 font-semibold' : 'text-gray-500'
                                    }`}
                                  >
                                    {conv.lastMessagePreview || 'Démarrer la conversation…'}
                                  </p>
                                  {unread > 0 && (
                                    <span className="flex-shrink-0 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold text-white bg-blue-600 rounded-full">
                                      {unread > 9 ? '9+' : unread}
                                    </span>
                                  )}
                                </div>
                                {conv.status === 'blocked' && (
                                  <p className="text-[10px] text-red-600 font-semibold mt-1 flex items-center gap-1">
                                    <Lock className="w-3 h-3" aria-hidden="true" /> Conversation bloquée
                                  </p>
                                )}
                              </div>
                            </div>
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                )}
              </div>
            </aside>

            {/* ─── Vue conversation ─── */}
            <section
              className={`flex flex-col min-h-[60vh] md:min-h-[70vh] ${
                !showListMobile ? 'block' : 'hidden md:flex'
              }`}
            >
              {!activeConv ? (
                <div className="flex-1 flex flex-col items-center justify-center p-10 text-center">
                  <MessageSquare className="w-12 h-12 text-gray-300 mb-3" aria-hidden="true" />
                  <p className="text-sm text-gray-600">
                    Sélectionnez une conversation pour commencer à échanger.
                  </p>
                </div>
              ) : (
                <>
                  {/* Header conversation */}
                  <header className="flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-100 bg-white">
                    <button
                      type="button"
                      onClick={() => setActiveConvId(null)}
                      className="md:hidden p-1.5 -ml-1.5 rounded hover:bg-gray-100 text-gray-700"
                      aria-label="Retour à la liste"
                    >
                      <ArrowLeft className="w-5 h-5" aria-hidden="true" />
                    </button>
                    <div
                      className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-[12px] font-bold ${avatarClassForRole(getPeerInfo(activeConv, userId).role)}`}
                      aria-hidden="true"
                    >
                      {getInitials(getPeerInfo(activeConv, userId).name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-gray-900 truncate">
                        {getPeerInfo(activeConv, userId).name}
                      </p>
                      {activeConv.context?.jobTitle ? (
                        <p className="text-[11px] text-gray-500 truncate">
                          À propos de : {activeConv.context.jobTitle}
                        </p>
                      ) : activeConv.context?.type === 'cvtheque' ? (
                        <p className="text-[11px] text-gray-500 truncate">CVthèque · profil disponible</p>
                      ) : null}
                    </div>
                    <div className="relative" ref={actionsRef}>
                      <button
                        type="button"
                        onClick={() => setShowActions((v) => !v)}
                        className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
                        aria-label="Plus d'actions"
                      >
                        <MoreVertical className="w-5 h-5" aria-hidden="true" />
                      </button>
                      {showActions && (
                        <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-10">
                          <button
                            type="button"
                            onClick={handleReport}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 text-left"
                          >
                            <Flag className="w-4 h-4" aria-hidden="true" />
                            Signaler
                          </button>
                          <button
                            type="button"
                            onClick={handleBlock}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 text-left"
                          >
                            <Lock className="w-4 h-4" aria-hidden="true" />
                            Bloquer
                          </button>
                        </div>
                      )}
                    </div>
                  </header>

                  {/* Bandeau anonymat CVthèque */}
                  {activeConv.context?.type === 'cvtheque' && !activeConv.anonymityLifted && (
                    <div className="px-4 py-3 bg-amber-50 border-b border-amber-100">
                      <div className="flex items-start gap-2">
                        <Shield className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" aria-hidden="true" />
                        <p className="text-[12px] text-amber-900 leading-relaxed">
                          <strong>Anonymat préservé.</strong>{' '}
                          {currentRole === 'candidate'
                            ? 'Votre identité (nom, email, téléphone) reste masquée tant que vous n\'avez pas accepté de la révéler. Évitez de partager vos coordonnées tant que vous n\'êtes pas certain.'
                            : "L'identité réelle du candidat reste masquée tant qu'il ne l'a pas révélée lui-même."}
                        </p>
                      </div>
                      {currentRole === 'candidate' && (
                        <div className="mt-2.5 pl-6">
                          <button
                            type="button"
                            onClick={handleRevealIdentity}
                            disabled={revealing}
                            className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-3.5 py-2 text-[13px] font-semibold text-white hover:bg-amber-700 disabled:opacity-60 disabled:cursor-not-allowed transition"
                          >
                            {revealing ? (
                              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                            ) : (
                              <BadgeCheck className="w-4 h-4" aria-hidden="true" />
                            )}
                            {revealing ? 'Révélation…' : 'Révéler mon identité'}
                          </button>
                          <p className="mt-1.5 text-[11px] text-amber-800/80">
                            Le recruteur verra alors votre nom complet. Action définitive pour cette conversation.
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Bandeau identité révélée (confirmation, recruteur & candidat) */}
                  {activeConv.context?.type === 'cvtheque' && activeConv.anonymityLifted && (
                    <div className="px-4 py-2 bg-emerald-50 border-b border-emerald-100 flex items-start gap-2">
                      <BadgeCheck className="w-4 h-4 text-emerald-700 flex-shrink-0 mt-0.5" aria-hidden="true" />
                      <p className="text-[12px] text-emerald-900 leading-relaxed">
                        <strong>Identité révélée.</strong>{' '}
                        {currentRole === 'candidate'
                          ? 'Le recruteur peut désormais voir votre nom complet.'
                          : 'Le candidat a révélé son identité dans cette conversation.'}
                      </p>
                    </div>
                  )}

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto px-4 py-4 bg-gray-50/40 space-y-3">
                    {activeMessages.length === 0 ? (
                      <p className="text-center text-sm text-gray-500 py-10">
                        Soyez le premier à écrire un message dans cette conversation.
                      </p>
                    ) : (
                      activeMessages.map((msg, idx) => {
                        const isMe = msg.senderId === userId
                        const prev = activeMessages[idx - 1]
                        const showDateSep =
                          !prev ||
                          new Date(prev.createdAt).toDateString() !==
                            new Date(msg.createdAt).toDateString()
                        const dateSep = showDateSep ? (
                          <div key={`sep-${msg._id}`} className="flex justify-center my-3">
                            <span className="px-3 py-1 rounded-full bg-gray-200/70 text-[11px] font-medium text-gray-600">
                              {formatDateSeparator(msg.createdAt)}
                            </span>
                          </div>
                        ) : null
                        if (msg.isSystemMessage) {
                          return (
                            <div key={msg._id}>
                              {dateSep}
                              <div className="flex justify-center">
                                <div className="flex items-start gap-2 max-w-[90%] rounded-xl bg-emerald-50 border border-emerald-100 px-3.5 py-2.5 text-[12px] font-medium text-emerald-900">
                                  <BadgeCheck className="w-4 h-4 flex-shrink-0 mt-0.5 text-emerald-600" aria-hidden="true" />
                                  <span className="break-words whitespace-pre-line leading-relaxed">
                                    {msg.content}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )
                        }
                        return (
                          <div key={msg._id}>
                            {dateSep}
                            <div className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                              <div
                                className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed shadow-sm ${
                                  isMe
                                    ? 'bg-blue-600 text-white rounded-br-md'
                                    : 'bg-white text-gray-900 border border-gray-100 rounded-bl-md'
                                }`}
                              >
                                <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                                <p
                                  className={`text-[10px] mt-1 text-right ${isMe ? 'text-blue-100' : 'text-gray-400'}`}
                                >
                                  {formatTime(msg.createdAt)}
                                  {msg.isFlagged && ' · ⚠️ contenu suspect'}
                                </p>
                              </div>
                            </div>
                          </div>
                        )
                      })
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Composer */}
                  {activeConv.status === 'blocked' ? (
                    <div className="px-4 py-3 border-t border-gray-100 bg-red-50 text-center">
                      <p className="text-sm text-red-700 font-semibold flex items-center justify-center gap-2">
                        <Lock className="w-4 h-4" aria-hidden="true" />
                        Cette conversation est bloquée.
                      </p>
                    </div>
                  ) : (
                    <form
                      onSubmit={handleSend}
                      className="px-3 py-3 border-t border-gray-100 bg-white"
                    >
                      <div className="flex items-end gap-2">
                        <textarea
                          ref={composerRef}
                          value={composerValue}
                          onChange={(e) => setComposerValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                              e.preventDefault()
                              handleSend()
                            }
                          }}
                          placeholder="Écrivez votre message…"
                          rows={1}
                          maxLength={4000}
                          className="flex-1 resize-none rounded-xl border border-gray-200 px-3 py-2.5 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[44px] max-h-[140px] overflow-y-auto"
                        />
                        <button
                          type="submit"
                          disabled={!composerValue.trim() || sending}
                          className="flex-shrink-0 inline-flex items-center justify-center gap-2 h-11 px-4 sm:px-5 rounded-xl bg-blue-600 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 active:scale-[0.98] disabled:bg-gray-300 disabled:cursor-not-allowed disabled:active:scale-100 transition"
                        >
                          {sending ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                              <span className="hidden sm:inline">Envoi…</span>
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4" aria-hidden="true" />
                              <span>Envoyer</span>
                            </>
                          )}
                        </button>
                      </div>
                      <div className="mt-1.5 flex items-center justify-between px-1">
                        <p className="text-[11px] text-gray-400">
                          <kbd className="font-sans">Ctrl/⌘ + Entrée</kbd> pour envoyer
                        </p>
                        <p className="text-[11px] text-gray-400 tabular-nums">
                          {composerValue.length}/4000
                        </p>
                      </div>
                    </form>
                  )}
                </>
              )}
            </section>
          </div>
        </div>
      </div>
    </Layout>
  )
}
